package dev.lingvo.site.pages.app.projects.slug.settings

import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxWidth
import com.varabyte.kobweb.compose.ui.modifiers.gap
import dev.lingvo.shared.ProviderSettingsDto
import dev.lingvo.site.api.ApiClient
import dev.lingvo.site.api.Async
import dev.lingvo.site.api.loadReloadable
import dev.lingvo.site.ds.AsyncFailureState
import dev.lingvo.site.ds.LBotIcon
import dev.lingvo.site.ds.LAlert
import dev.lingvo.site.ds.LAlertVariant
import dev.lingvo.site.ds.LToastVariant
import dev.lingvo.site.ds.LoadingState
import dev.lingvo.site.ds.Space
import dev.lingvo.site.i18n.t

@Composable
internal fun ProjectProviderSettings(
    slug: String,
    onChanged: () -> Unit = {},
    notify: (String, LToastVariant, Long) -> Int,
    loadProviders: suspend (String) -> List<ProviderSettingsDto> = ApiClient::listProviders,
) {
    var refreshKey by remember(slug) { mutableStateOf(0) }
    // **A token, not a key.** Saving a credential replayed the whole provider grid's staggered
    // entrance over the field the reader had just typed into — the panel dropped to a four-row
    // skeleton and every card re-entered, because a changed key discards the value before the
    // request is even sent. The grid stays put now and only its contents change.
    val reloadable = loadReloadable(slug, reloadToken = refreshKey) { loadProviders(slug) }

    when (val state = reloadable.state) {
        Async.Loading -> ProviderLoadFrame {
            LoadingState(rows = 4, label = t("app.projects.slug.settings.project_provider_settings.loading_translation_providers", "Loading translation providers"))
        }
        is Async.Failure -> ProviderLoadFrame {
            AsyncFailureState(state, onRetry = { refreshKey++ })
        }
        is Async.Success -> Column(Modifier.fillMaxWidth().gap(Space.MD)) {
            // A save that succeeded and a reload that then failed leaves the grid showing the
            // credential as it was before — contradicting the success toast the reader just saw,
            // with nothing to say which of the two is true. Keeping the grid is right; keeping it
            // silently is not.
            reloadable.refreshError?.let { failure ->
                LAlert(
                    t(
                        "app.projects.slug.settings.project_provider_settings.showing_the_last_settings",
                        "Showing the last settings that loaded",
                    ),
                    failure.readable(),
                    modifier = Modifier.fillMaxWidth(),
                    variant = LAlertVariant.WARNING,
                    live = true,
                )
            }
            ProjectProviderGrid(
                providers = state.value,
                slug = slug,
                onChanged = {
                    refreshKey++
                    onChanged()
                },
                notify = notify,
            )
        }
    }
}

@Composable
private fun ProviderLoadFrame(content: @Composable () -> Unit) {
    SettingsSection(
        title = t("app.projects.slug.settings.project_provider_settings.translation_providers", "Translation providers"),
        description = t("app.projects.slug.settings.project_provider_settings.choose_where_each_project_credential_comes", "Choose where each project credential comes from. Secrets remain write-only."),
        wide = true,
        icon = { LBotIcon() },
        content = content,
    )
}
